import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Ng2IzitoastService } from 'ng2-izitoast';
import { ClientesService } from 'src/app/services/clientes.service';
import { LoginService } from 'src/app/services/login.service';
import { PaquetesService } from 'src/app/services/paquetes.service';
import { environment } from '../../../environments/environment';

interface FlowStep {
  label: string;
  helper: string;
}

interface DemoFamily {
  id: number;
  nombre: string;
  descripcion: string;
  imagen: string;
  productos?: number;
}

interface DemoPackage {
  id: number;
  familyId: number;
  nombre: string;
  descripcion: string;
  precio: number;
  imagen: string;
  incluye: string[];
  productos: DemoProduct[];
}

interface DemoVariation {
  id: number;
  nombre: string;
  descripcion: string;
  imagenes: string[];
  variacionId?: number;
  variacionLabel?: string;
}

interface DemoProduct {
  id: number;
  nombre: string;
  descripcion: string;
  imagen: string;
  selectedVariationId: number | null;
  variaciones: DemoVariation[];
}

interface DemoImage {
  images: string[];
  index: number;
  title: string;
  caption: string;
}

interface BreakdownItem {
  label: string;
  detail: string;
  amount: number;
}

@Component({
  selector: 'app-v2proceso-demo',
  templateUrl: './v2proceso-demo.component.html',
  styleUrls: ['./v2proceso-demo.component.scss']
})
export class V2procesoDemoComponent implements OnInit {

  public activeStepIndex = 0;
  public activeProductId: number | null = null;
  public selectedImage: DemoImage | null = null;
  public carouselIndexes: { [variationId: number]: number } = {};
  public families: DemoFamily[] = [];
  public packages: DemoPackage[] = [];
  public selectedFamilyId: number | null = null;
  public selectedPackageId: number | null = null;
  public extras: BreakdownItem[] = [];
  public clienteId: number | null = null;
  public usuarioActual: any = null;
  public loadingInitial = true;
  public loadingPackages = false;
  public savingPackage = false;
  public savingVariation = false;
  public errorMessage = '';
  public readonly environmentBase = environment.BASE_URL;
  private readonly fallbackImage = 'assets/img/auri_logo.png';

  public readonly steps: FlowStep[] = [
    {
      label: 'Escoger familia',
      // helper: 'Familias asignadas al grupo del alumno.'
      helper: ''
    },
    {
      label: 'Escoger paquete',
      // helper: 'Paquetes disponibles segun familia seleccionada.'
      helper: ''
    },
    {
      label: 'Personalizar productos',
      // helper: 'Cada producto lleva a sus variaciones disponibles.'
      helper: ''
    },
    {
      label: 'Desglose',
      // helper: 'Resumen de paquete, productos configurados y total.'
      helper: ''
    },
    {
      label: 'Pagar',
      // helper: 'Pantalla final para confirmar pago.'
      helper: ''
    }
  ];

  constructor(
    private modalService: NgbModal,
    private paquetesService: PaquetesService,
    private clientesService: ClientesService,
    private loginService: LoginService,
    private router: Router,
    private iziToast: Ng2IzitoastService
  ) { }

  ngOnInit(): void {
    if (!localStorage.getItem('token')) {
      this.router.navigate(['/cliente/login'], { queryParams: { redirect: '/cliente/proceso-v2' } });
      return;
    }
    this.loadClienteData();
  }

  public get currentStep(): FlowStep {
    return this.steps[this.activeStepIndex];
  }

  public get selectedFamily(): DemoFamily {
    return this.families.find((familia) => familia.id === this.selectedFamilyId) || this.families[0] || {
      id: 0,
      nombre: '',
      descripcion: '',
      imagen: this.fallbackImage
    };
  }

  public get familyPackages(): DemoPackage[] {
    return this.packages.filter((paquete) => paquete.familyId === this.selectedFamilyId);
  }

  public get selectedPackage(): DemoPackage {
    return this.packages.find((paquete) => paquete.id === this.selectedPackageId) || this.familyPackages[0] || this.packages[0] || {
      id: 0,
      familyId: 0,
      nombre: '',
      descripcion: '',
      precio: 0,
      imagen: this.fallbackImage,
      incluye: [],
      productos: []
    };
  }

  public get currentProducts(): DemoProduct[] {
    return this.selectedPackage.productos || [];
  }

  public get activeProduct(): DemoProduct | null {
    return this.currentProducts.find((product) => product.id === this.activeProductId) || null;
  }

  public get completedProductCount(): number {
    return this.currentProducts.filter((product) => this.isProductComplete(product)).length;
  }

  public get subtotal(): number {
    return this.selectedPackage.precio + this.extras.reduce((total, item) => total + item.amount, 0);
  }

  public get paymentTotal(): number {
    return Math.max(this.subtotal, 0);
  }

  public selectStep(index: number): void {
    if (index < 0 || index >= this.steps.length) {
      return;
    }
    this.activeProductId = null;
    this.activeStepIndex = index;
  }

  public nextStep(): void {
    this.selectStep(this.activeStepIndex + 1);
  }

  public previousStep(): void {
    this.selectStep(this.activeStepIndex - 1);
  }

  public selectFamily(familia: DemoFamily): void {
    this.selectedFamilyId = familia.id;
    this.activeProductId = null;
    this.selectedPackageId = null;
    this.loadPackagesForFamily(familia.id);
  }

  public selectPackage(paquete: DemoPackage): void {
    if (!this.clienteId || this.savingPackage) {
      return;
    }
    this.selectedPackageId = paquete.id;
    this.activeProductId = null;
    this.savingPackage = true;
    this.clientesService.agregarPaqueteClienteCatalogoV2({
      paquete_v2: paquete.id,
      cliente: this.clienteId
    }).subscribe({
      next: (response: any) => {
        this.savingPackage = false;
        if (response?.success) {
          this.toast('Paquete guardado.');
          this.refreshSavedSelections();
          this.activeStepIndex = 2;
          return;
        }
        this.toast(response?.error || 'No se pudo guardar el paquete.', true);
      },
      error: () => {
        this.savingPackage = false;
        this.toast('No se pudo guardar el paquete.', true);
      }
    });
  }

  public openProduct(product: DemoProduct): void {
    this.activeProductId = product.id;
  }

  public backToProductList(): void {
    this.activeProductId = null;
  }

  public selectVariation(product: DemoProduct, variation: DemoVariation): void {
    if (!this.clienteId || this.savingVariation) {
      return;
    }
    const previousVariation = product.selectedVariationId;
    product.selectedVariationId = variation.id;
    this.savingVariation = true;
    this.clientesService.seleccionarConfiguracionClienteV2({
      configuracion: variation.id,
      cliente: this.clienteId
    }).subscribe({
      next: (response: any) => {
        this.savingVariation = false;
        if (response?.success) {
          this.toast('Seleccion guardada.');
          this.activeProductId = null;
          this.refreshSavedSelections();
          return;
        }
        product.selectedVariationId = previousVariation;
        this.toast(response?.error || 'No se pudo guardar la seleccion.', true);
      },
      error: () => {
        this.savingVariation = false;
        product.selectedVariationId = previousVariation;
        this.toast('No se pudo guardar la seleccion.', true);
      }
    });
  }

  public selectActiveVariation(variation: DemoVariation): void {
    if (!this.activeProduct) {
      return;
    }
    this.selectVariation(this.activeProduct, variation);
  }

  public isProductComplete(product: DemoProduct): boolean {
    return !product.variaciones.length || product.selectedVariationId !== null;
  }

  public getSelectedVariation(product: DemoProduct): DemoVariation | null {
    if (product.selectedVariationId === null) {
      return null;
    }
    return product.variaciones.find((variation) => variation.id === product.selectedVariationId) || null;
  }

  public getVariationImage(variation: DemoVariation): string {
    const index = this.getVariationImageIndex(variation);
    return variation.imagenes[index] || variation.imagenes[0] || this.fallbackImage;
  }

  public getVariationImageIndex(variation: DemoVariation): number {
    const rawIndex = this.carouselIndexes[variation.id] || 0;
    return this.normalizeIndex(rawIndex, variation.imagenes.length);
  }

  public previousVariationImage(variation: DemoVariation, event: Event): void {
    event.stopPropagation();
    this.setVariationImageIndex(variation, this.getVariationImageIndex(variation) - 1);
  }

  public nextVariationImage(variation: DemoVariation, event: Event): void {
    event.stopPropagation();
    this.setVariationImageIndex(variation, this.getVariationImageIndex(variation) + 1);
  }

  public openGallery(modal: any, images: string[], title: string, caption: string, index = 0): void {
    const galleryImages = images?.length ? images : [this.fallbackImage];
    this.selectedImage = {
      images: galleryImages,
      index: this.normalizeIndex(index, galleryImages.length),
      title,
      caption
    };
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  public get modalImageSrc(): string {
    if (!this.selectedImage || !this.selectedImage.images.length) {
      return '';
    }
    return this.selectedImage.images[this.selectedImage.index] || this.selectedImage.images[0];
  }

  public previousModalImage(): void {
    if (!this.selectedImage) {
      return;
    }
    this.selectedImage.index = this.normalizeIndex(this.selectedImage.index - 1, this.selectedImage.images.length);
  }

  public nextModalImage(): void {
    if (!this.selectedImage) {
      return;
    }
    this.selectedImage.index = this.normalizeIndex(this.selectedImage.index + 1, this.selectedImage.images.length);
  }

  public dismissAll(): void {
    this.modalService.dismissAll();
  }

  public logout(): void {
    this.loginService.cerrarSesion();
    this.router.navigate(['/cliente/login']);
  }

  public goToPayment(): void {
    this.router.navigate(['/cliente/realizar-pago']);
  }

  public get nombrePerfil(): string {
    if (!this.usuarioActual) {
      return 'Mi cuenta';
    }
    return [
      this.usuarioActual.first_name || this.usuarioActual.nombre,
      this.usuarioActual.last_name,
      this.usuarioActual.apellido_materno
    ].filter(Boolean).join(' ') || 'Mi cuenta';
  }

  private loadClienteData(): void {
    this.loadingInitial = true;
    this.errorMessage = '';
    this.clientesService.getDataUsuario().subscribe({
      next: (response: any) => {
        if (!response?.success) {
          this.handleSessionError();
          return;
        }
        this.usuarioActual = response.content;
        this.clienteId = response.content?.id || null;
        this.loadFamilies();
      },
      error: () => this.handleSessionError()
    });
  }

  private handleSessionError(): void {
    this.loadingInitial = false;
    this.loginService.cerrarSesion();
    this.router.navigate(['/cliente/login'], { queryParams: { redirect: '/cliente/proceso-v2' } });
  }

  private loadFamilies(): void {
    this.paquetesService.getFamiliasClienteV2().subscribe({
      next: (response: any) => {
        this.loadingInitial = false;
        if (response?.success) {
          this.families = (response.content || []).map((familia) => this.mapFamily(familia));
          return;
        }
        this.errorMessage = response?.error || 'No se pudieron cargar las familias.';
      },
      error: () => {
        this.loadingInitial = false;
        this.errorMessage = 'No se pudieron cargar las familias.';
      }
    });
  }

  private loadPackagesForFamily(familyId: number): void {
    this.loadingPackages = true;
    this.errorMessage = '';
    this.paquetesService.getPaquetesFamiliaClienteV2(familyId).subscribe({
      next: (response: any) => {
        this.loadingPackages = false;
        if (response?.success) {
          const loadedPackages = (response.content || []).map((paquete) => this.mapPackage(paquete));
          this.packages = [
            ...this.packages.filter((paquete) => paquete.familyId !== familyId),
            ...loadedPackages
          ];
          this.activeStepIndex = 1;
          return;
        }
        this.toast(response?.error || 'No se pudieron cargar los paquetes.', true);
      },
      error: () => {
        this.loadingPackages = false;
        this.toast('No se pudieron cargar los paquetes.', true);
      }
    });
  }

  private refreshSavedSelections(): void {
    this.clientesService.getPersonalizacionClienteV2().subscribe({
      next: (response: any) => {
        if (response?.success) {
          this.applySavedSelections(response.seleccionadas || []);
        }
      }
    });
  }

  private applySavedSelections(seleccionadas: any[]): void {
    const byProduct: { [productId: number]: number } = {};
    seleccionadas.forEach((seleccion) => {
      const productId = this.toNumber(seleccion.producto);
      const configId = this.toNumber(seleccion.configuracion);
      if (productId && configId) {
        byProduct[productId] = configId;
      }
    });
    this.packages.forEach((paquete) => {
      paquete.productos.forEach((producto) => {
        if (byProduct[producto.id]) {
          producto.selectedVariationId = byProduct[producto.id];
        }
      });
    });
  }

  private mapFamily(familia: any): DemoFamily {
    return {
      id: this.toNumber(familia.id),
      nombre: familia.nombre || 'Familia',
      descripcion: familia.descripcion || '',
      imagen: this.buildImagePath(familia.imagen),
      productos: this.toNumber(familia.productos)
    };
  }

  private mapPackage(paquete: any): DemoPackage {
    const productos = (paquete.productos || []).map((producto) => this.mapProduct(producto));
    return {
      id: this.toNumber(paquete.id),
      familyId: this.toNumber(paquete.familia),
      nombre: paquete.nombre || 'Paquete',
      descripcion: paquete.descripcion || '',
      precio: this.toMoney(paquete.precio),
      imagen: this.buildImagePath(paquete.imagen),
      incluye: productos.map((producto) => producto.nombre),
      productos
    };
  }

  private mapProduct(producto: any): DemoProduct {
    const productImages = this.getImagesFromCollection(producto.imagenes || []);
    const variaciones: DemoVariation[] = [];
    (producto.variaciones || []).forEach((variacion) => {
      (variacion.configuraciones || []).forEach((configuracion) => {
        const images = this.getImagesFromConfig(configuracion);
        variaciones.push({
          id: this.toNumber(configuracion.id),
          nombre: configuracion.configuracion || variacion.label || variacion.nombre || 'Opcion',
          descripcion: configuracion.descripcion || '',
          imagenes: images.length ? images : productImages,
          variacionId: this.toNumber(variacion.variacion || variacion.id),
          variacionLabel: variacion.label || variacion.nombre || ''
        });
      });
    });

    return {
      id: this.toNumber(producto.id),
      nombre: producto.nombre || 'Producto',
      descripcion: producto.descripcion || '',
      imagen: this.buildImagePath(producto.imagen || productImages[0]),
      selectedVariationId: null,
      variaciones
    };
  }

  private getImagesFromConfig(configuracion: any): string[] {
    const images: string[] = [];
    if (configuracion?.imagen) {
      images.push(this.buildImagePath(configuracion.imagen));
    }
    return images.concat(this.getImagesFromCollection(configuracion?.imagenes || []));
  }

  private getImagesFromCollection(items: any[]): string[] {
    return (items || [])
      .map((item) => item?.imagen)
      .filter(Boolean)
      .map((imagen) => this.buildImagePath(imagen));
  }

  private buildImagePath(imagen?: string): string {
    if (!imagen) {
      return this.fallbackImage;
    }
    if (imagen.indexOf('http') === 0 || imagen.indexOf('assets/') === 0) {
      return imagen;
    }
    return `${this.environmentBase}${imagen}`;
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private toMoney(value: any): number {
    const parsed = Number(String(value || 0).replace(/[^0-9.-]/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private toast(message: string, error = false): void {
    this.iziToast.show({
      title: message,
      titleColor: '#ffffff',
      color: error ? '#CA0B00' : '#4CAF50',
      position: 'topRight',
      closeOnEscape: true
    });
  }

  private setVariationImageIndex(variation: DemoVariation, index: number): void {
    this.carouselIndexes[variation.id] = this.normalizeIndex(index, variation.imagenes.length);
  }

  private normalizeIndex(index: number, length: number): number {
    if (!length) {
      return 0;
    }
    return (index + length) % length;
  }
}
